import { DecimalPipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { formatearErrorStock } from '@shared/utils/functions.utils';
import {
  FormField,
  disabled,
  form,
  maxLength,
  min,
  pattern,
  required,
} from '@angular/forms/signals';
import { FormsModule } from '@angular/forms';
import { ETipoUnidadSUNAT } from '@core/enums/tipoUnidadSUNAT.enum';
import { ETipoProducto } from '@core/enums/tipoProducto.enum';
import { unidadesCompatibles, convertirUnidad } from '@core/enums/unidad-compatibles.function';
import { ICategoriaData } from '@core/interfaces/entidades/categoria.entidad';
import { IProductoData } from '@core/interfaces/entidades/producto.entidad';
import { IOptionsSelect } from '@core/interfaces/options.select.interface';
import { ICrearProductoReq } from '@core/interfaces/request-response/producto.dto.interface';
import { IRecetaDetalleReq } from '@core/interfaces/request-response/receta.dto.interface';
import { ProductoApiService } from '@core/services/api/producto.service';
import { RecetaApiService } from '@core/services/api/receta-api.service';
import { TipoUnidadSunatPipe } from '@shared/pipes/tipo-unidad-sunat.pipe';
import { ProductoImagenService } from '@shared/services/producto-imagen.service';
import { ToastService } from '@shared/services/toast.service';
import { UiButton, UiDialog, UiInputNumber, UiSelect, type SelectOption } from '@shared/components/ui';
import { CrearCategoria } from '../crear-categoria/crear-categoria';
import { ProductoAvatar } from '../producto-avatar/producto-avatar';
import { FormErrorMessagePipe } from '@shared/pipes/form-error-message.pipe';

@Component({
  selector: 'app-crear-producto',
  imports: [
    FormField,
    FormsModule,
    UiSelect,
    UiButton,
    UiInputNumber,
    UiDialog,
    CrearCategoria,
    ProductoAvatar,
    FormErrorMessagePipe,
    DecimalPipe,
  ],
  providers: [TipoUnidadSunatPipe],
  templateUrl: './crear-producto.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './crear-producto.css',
})
export class CrearProducto implements OnInit, OnDestroy {
  ngOnInit(): void {
    this.opcionesCategorias.set(this.categorias().map((c) => ({ label: c.nombre, value: c.id })));
    this.opcionesTipoUnidad.set(
      Object.values(ETipoUnidadSUNAT).map((u) => ({
        label: this.tipoUnidadSunatPipe
          .transform(u)
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase()),
        value: u,
      })),
    );
    if (this.tipoInicial()) {
      this.formProducto.tipo_producto().value.set(this.tipoInicial()!);
      if (this.tipoInicial() === ETipoProducto.RECETA) {
        this.formProducto.controlar_stock().value.set(false);
      }
    }
    const codigoInicial = this.codigoInicial()?.trim();
    if (codigoInicial) {
      this.generarCodigo.set(false);
      this.formProducto.codigo().value.set(codigoInicial);
    }
  }

  private tipoUnidadSunatPipe = inject(TipoUnidadSunatPipe);
  private toastService = inject(ToastService);
  private productoApiService = inject(ProductoApiService);
  private productoImagenService = inject(ProductoImagenService);
  private recetaApiService = inject(RecetaApiService);

  opcionesTipoUnidad = signal<IOptionsSelect<ETipoUnidadSUNAT>[]>([]);
  opcionesCategorias = signal<IOptionsSelect<number>[]>([]);
  modalCrearCategoria = signal(false);
  opcionesTipoProducto = signal<IOptionsSelect<ETipoProducto>[]>([
    { label: 'Producto', value: ETipoProducto.PRODUCTO },
    { label: 'Ingrediente', value: ETipoProducto.INGREDIENTE },
    { label: 'Receta', value: ETipoProducto.RECETA },
  ]);

  categorias = input<ICategoriaData[]>([]);
  tipoInicial = input<ETipoProducto | null>(null);
  /** Código precargado, por ejemplo cuando se viene del escáner de inventario. */
  codigoInicial = input<string | null>(null);
  /**
   * Cuando se crea desde el ingreso de inventario, el producto se registra con
   * stock 0 y el stock digitado se envía al padre para ingresarlo como
   * movimiento de inventario. Así no se duplica el stock al registrar el ingreso.
   */
  crearConStockCero = input(false);

  /** Mantiene el select sincronizado si las categorías llegan después de abrir el modal. */
  private readonly sincronizarCategorias = effect(() => {
    this.opcionesCategorias.set(
      this.categorias().map((c) => ({ label: c.nombre, value: c.id })),
    );
  });
  generarCodigo = signal(false);
  maxImageSizeMb = signal(this.productoImagenService.defaultMaxSizeMb);
  imagenArchivo = signal<File | null>(null);
  imagenError = signal<string | null>(null);

  productoSignal = signal({
    codigo: '',
    nombre: '',
    descripcion: '',
    precio_venta: 0,
    precio_base: 0,
    precio_venta_sucursal: null as number | null,
    unidad_medida: ETipoUnidadSUNAT.UNIDAD_BIENES,
    stock: 0,
    categoria_id: null as number | null,
    url_imagen: '',
    controlar_stock: true,
    es_servicio: false,
    es_gestion_tiempo: false,
    duracion_minutos: 0,
    tipo_producto: ETipoProducto.PRODUCTO,
  });

  formProducto = form(this.productoSignal, (schemaPath) => {
    required(schemaPath.nombre, { message: 'El nombre es obligatorio.' });
    required(schemaPath.precio_base, { message: 'El precio base es obligatorio.' });
    required(schemaPath.precio_venta, { message: 'El precio de venta es obligatorio.' });
    required(schemaPath.unidad_medida, { message: 'La unidad de medida es obligatoria.' });
    required(schemaPath.stock, { message: 'El stock es obligatorio.' });
    maxLength(schemaPath.codigo, 20, {
      message: 'El código no puede exceder los 20 caracteres.',
    });
    maxLength(schemaPath.nombre, 50, {
      message: 'El nombre no puede exceder los 50 caracteres.',
    });
    maxLength(schemaPath.descripcion, 125, {
      message: 'La descripción no puede exceder los 125 caracteres.',
    });
    min(schemaPath.precio_venta, 0, { message: 'El precio de venta no puede ser negativo.' });
    min(schemaPath.precio_base, 0, { message: 'El precio base no puede ser negativo.' });
    min(schemaPath.stock, 0, { message: 'El stock no puede ser negativo.' });
    disabled(schemaPath.codigo, this.generarCodigo);
    required(schemaPath.codigo, {
      message: 'El código es obligatorio.',
      when: () => !this.generarCodigo() && this.formProducto.codigo().touched(),
    });
    pattern(schemaPath.codigo, /^[a-zA-Z0-9_-]+$/, {
      message: 'El código solo puede contener letras, números, guiones y guiones bajos.',
    });

    disabled(schemaPath.controlar_stock, () => {
      // Leemos el valor del formulario de manera segura y tipada usando el método .value() del control final
      return this.formProducto.tipo_producto().value() === ETipoProducto.RECETA;
    });
  });
  crear = output<IProductoData | undefined>();
  /** Stock digitado que el padre debe ingresar después de crear el producto. */
  stockInicial = output<number>();

  productosIngrediente = signal<IProductoData[]>([]);

  readonly opcionesProductosIngrediente = computed<SelectOption[]>(() =>
    this.productosIngrediente().map((p) => ({ label: `${p.nombre} (${p.codigo})`, value: p })),
  );

  readonly compareProductoById = (a: any, b: any) => a?.id === b?.id;

  private tempIdCounter = 0;
  private stockIngresado = 0;
  guardando = signal(false);

  ingredientes: {
    producto: IProductoData | null;
    cantidad: number;
    unidad: ETipoUnidadSUNAT;
    costoUnitario: number;
    tempId: number;
  }[] = [];

  ngOnDestroy(): void {}

  isTipoProducto = computed(() => {
    return this.formProducto.tipo_producto().value() === ETipoProducto.PRODUCTO;
  });

  isTipoReceta = computed(() => {
    return this.formProducto.tipo_producto().value() === ETipoProducto.RECETA;
  });

  onTipoProductoChange(value: ETipoProducto) {
    if (value === ETipoProducto.RECETA) {
      this.formProducto.controlar_stock().value.set(false);
    }
  }

  onChangeGenerarCodigo(checked: boolean) {
    this.generarCodigo.set(checked);
    if (checked) this.formProducto.codigo().value.set('');
  }

  onChildImagenChange(file: File | null) {
    this.imagenArchivo.set(file);
    if (!file) this.imagenError.set(null);
  }

  checkInvalidForm(): boolean {
    if (this.formProducto().invalid()) {
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'Por favor, complete los campos obligatorios.',
      );
      return true;
    }
    return false;
  }

  unidadesMedida = [
    { value: ETipoUnidadSUNAT.GRAMO, label: 'Gramos (g)' },
    { value: ETipoUnidadSUNAT.KILOGRAMO, label: 'Kilogramos (kg)' },
    { value: ETipoUnidadSUNAT.MILILITRO, label: 'Mililitros (ml)' },
    { value: ETipoUnidadSUNAT.LITRO, label: 'Litros (L)' },
    { value: ETipoUnidadSUNAT.UNIDAD_BIENES, label: 'Unidades (u)' },
  ];

  unidadesCompatiblesParaIngrediente(ing: {
    producto: IProductoData | null;
    unidad: ETipoUnidadSUNAT;
  }): { value: ETipoUnidadSUNAT; label: string }[] {
    const ref = ing.producto?.unidad_medida || ing.unidad;
    const compatibles = unidadesCompatibles(ref);
    return this.unidadesMedida.filter((u) => compatibles.includes(u.value));
  }

  cargarIngredientes() {
    if (this.productosIngrediente().length > 0) return;
    this.productoApiService.paginar(1, 200, undefined, ETipoProducto.INGREDIENTE).subscribe({
      next: (r) => this.productosIngrediente.set(r.data.filter((p) => p.activo)),
    });
  }

  agregarIngrediente() {
    this.ingredientes.push({
      producto: null,
      cantidad: 0,
      unidad: ETipoUnidadSUNAT.GRAMO,
      costoUnitario: 0,
      tempId: this.tempIdCounter++,
    });
  }

  removerIngrediente(index: number) {
    this.ingredientes.splice(index, 1);
  }

  onIngredienteSeleccionado(index: number) {
    const ing = this.ingredientes[index];
    if (ing.producto) {
      ing.unidad = ing.producto.unidad_medida;
      this.actualizarCostoIngrediente(index);
    }
  }

  onUnidadIngredienteCambiada(index: number) {
    this.actualizarCostoIngrediente(index);
  }

  private actualizarCostoIngrediente(index: number) {
    const ing = this.ingredientes[index];
    if (!ing.producto) return;
    const factor = convertirUnidad(1, ing.unidad, ing.producto.unidad_medida);
    ing.costoUnitario = ing.producto.precio_base * factor;
  }

  costoTotal(): number {
    return this.ingredientes.reduce(
      (sum, ing) => sum + (ing.costoUnitario || 0) * (ing.cantidad || 0),
      0,
    );
  }

  onCrear($event: Event) {
    $event.preventDefault();
    if (this.checkInvalidForm()) return;
    if (this.imagenError()) {
      this.toastService.show('top-right', 'error', 'Error', this.imagenError()!);
      return;
    }
    if (this.isTipoReceta() && this.ingredientes.length === 0) {
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'Una receta necesita al menos un ingrediente.',
      );
      return;
    }

    const fv = this.formProducto().controlValue();
    this.stockIngresado = fv.stock;
    const productoData: ICrearProductoReq = {
      codigo: fv.codigo || undefined,
      nombre: fv.nombre,
      descripcion: fv.descripcion,
      precio_venta: fv.precio_venta,
      precio_base: fv.precio_base,
      precio_venta_sucursal: fv.precio_venta_sucursal || null,
      unidad_medida: fv.unidad_medida,
      stock: this.crearConStockCero() ? 0 : fv.stock,
      categoria_id: fv.categoria_id,
      url_imagen: fv.url_imagen || undefined,
      controlar_stock: fv.controlar_stock,
      es_servicio: fv.es_servicio,
      es_gestion_tiempo: fv.es_gestion_tiempo,
      duracion_minutos: fv.es_gestion_tiempo ? fv.duracion_minutos : undefined,
      tipo_producto: fv.tipo_producto,
    };

    const payload = this.imagenArchivo()
      ? this.productoImagenService.buildFormData(productoData, {
          file: this.imagenArchivo()!,
          fileFieldName: 'imagen',
        })
      : productoData;

    this.guardando.set(true);
    this.productoApiService.crear(payload).subscribe({
      next: (data) => {
        if (this.isTipoReceta() && this.ingredientes.length > 0) {
          this.crearRecetaParaProducto(data);
        } else {
          this.guardando.set(false);
          this.toastService.show('top-right', 'success', 'Éxito', 'Producto creado correctamente.');
          this.emitirProductoCreado(data);
        }
      },
      error: (err) => {
        this.guardando.set(false);
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message ? formatearErrorStock(err.error.message) : 'No se pudo crear el producto.',
        );
      },
    });
  }

  private crearRecetaParaProducto(producto: IProductoData) {
    const detallesReq: IRecetaDetalleReq[] = this.ingredientes.map((ing) => ({
      ingrediente_producto_id: ing.producto!.id,
      cantidad_requerida: ing.cantidad,
      unidad_medida: ing.unidad,
    }));

    this.recetaApiService
      .crear({
        producto_receta_id: producto.id,
        detalles: detallesReq,
      })
      .subscribe({
        next: () => {
          this.guardando.set(false);
          this.toastService.show(
            'top-right',
            'success',
            'Éxito',
            'Producto y receta creados correctamente.',
          );
          this.emitirProductoCreado(producto);
        },
        error: (err) => {
          this.guardando.set(false);
          if (err.status == 401) return;
          this.toastService.show(
            'top-right',
            'warn',
            'Producto creado, receta pendiente',
            err.error.message ? formatearErrorStock(err.error.message) : 'El producto se creó pero no se pudo crear la receta.',
          );
          this.emitirProductoCreado(producto);
        },
      });
  }

  private emitirProductoCreado(producto: IProductoData) {
    if (this.crearConStockCero()) {
      this.stockInicial.emit(this.stockIngresado);
    }
    this.crear.emit(producto);
  }

  onCerrar() {
    this.crear.emit(undefined);
  }

  onCrearCategoriaCerrada(data?: ICategoriaData) {
    this.modalCrearCategoria.set(false);
    if (data) {
      this.opcionesCategorias.update((opts) => [...opts, { label: data.nombre, value: data.id }]);
      this.formProducto.categoria_id().value.set(data.id);
    }
  }
}
